import { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTimes, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import TablesService from '../../../../../../../../AppSettings/services/TablesService';
import MatterService from '../../../../../../../../AppSettings/services/MatterService';
import type { CustomTableHolding, CustomTable } from '@frontend/Models/Table/CustomTableModels';
import './CustomTables.css';

type TableWithColumns = CustomTableHolding & { columns?: unknown[]; rows?: unknown[] };

const parseJSON = (val: string | unknown): unknown[] => {
    try {
        return typeof val === 'string' ? JSON.parse(val) : (Array.isArray(val) ? val : []);
    } catch {
        return [];
    }
};

const getTableAbbreviation = (tableName: string | undefined): string => {
    if (!tableName) return 'T';
    return tableName
        .split('_')
        .map(word => word.charAt(0).toUpperCase())
        .join('')
        .substring(0, 2);
};

const formatTableName = (tableName: string | undefined): string => {
    if (!tableName) return '';
    return tableName
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export interface CustomTablesScreenProps {
    matterId: string;
    onTableSelect: (table: TableWithColumns, idx: number) => void;
    onLoadingChange: (loading: boolean) => void;
}

const CustomTablesScreen: React.FC<CustomTablesScreenProps> = ({ matterId, onTableSelect, onLoadingChange }) => {
    const [customTables, setCustomTables] = useState<CustomTableHolding[]>([]);
    const [selectedIdx, setSelectedIdx] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [matterName, setMatterName] = useState('');

    const [showAddTableModal, setShowAddTableModal] = useState(false);
    const [availableTables, setAvailableTables] = useState<CustomTable[]>([]);
    const [loadingAvailableTables, setLoadingAvailableTables] = useState(false);
    const [selectedTablesToAdd, setSelectedTablesToAdd] = useState<number[]>([]);
    const bannerScrollRef = useRef<HTMLDivElement>(null);

    const fetchCustomTables = useCallback(async () => {
        try {
            setLoading(true);
            const response = await TablesService.getCustomTablesHoldingByMatterId(matterId) as CustomTableHolding[] | { data?: CustomTableHolding[] };
            const tables = Array.isArray(response) ? response : (response?.data ?? []);
            const sortedTables = [...tables].sort((a, b) => {
                const nameA = (a.table_name || '').toLowerCase();
                const nameB = (b.table_name || '').toLowerCase();
                return nameA.localeCompare(nameB);
            });
            setCustomTables(sortedTables);
            setError(null);
        } catch (err) {
            setError('Failed to fetch custom tables');
            console.error('Error fetching custom tables:', err);
        } finally {
            setLoading(false);
        }
    }, [matterId]);

    const fetchMatterName = useCallback(async () => {
        try {
            const matterDetails = await MatterService.getMatterDetails(matterId) as Record<string, unknown>;
            const name = (matterDetails?.matter_name ?? matterDetails?.name ?? matterDetails?.matterName ?? matterDetails?.project_name ?? matterDetails?.title ?? matterDetails?.client_name ?? `Matter ${matterId}`) as string;
            setMatterName(name);
        } catch (err) {
            console.error('Error fetching matter name:', err);
            setMatterName(`Matter ${matterId}`);
        }
    }, [matterId]);

    useEffect(() => {
        fetchCustomTables();
        fetchMatterName();
    }, [matterId, fetchCustomTables, fetchMatterName]);

    useEffect(() => {
        if (customTables.length > 0 && selectedIdx < customTables.length) {
            const table = customTables[selectedIdx];
            const columns = parseJSON(table.column_value);
            const rows = parseJSON(table.rows_value);
            onTableSelect({ ...table, columns, rows }, selectedIdx);
        }
    }, [selectedIdx, customTables, onTableSelect]);

    useEffect(() => {
        onLoadingChange(loading);
    }, [loading, onLoadingChange]);

    useEffect(() => {
        const scrollContainer = bannerScrollRef.current;
        if (!scrollContainer) return;
        const selectedTab = scrollContainer.querySelector<HTMLElement>('.custom-tables-banner-tab.is-selected');
        if (selectedTab && typeof selectedTab.scrollIntoView === 'function') {
            selectedTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
    }, [selectedIdx, customTables.length]);

    const fetchAvailableTables = async (): Promise<void> => {
        try {
            setLoadingAvailableTables(true);
            const response = await TablesService.getAvailableTablesForMatter(matterId);
            const list = Array.isArray(response) ? response : (response?.available_tables ?? []);
            setAvailableTables(list);
            setSelectedTablesToAdd([]);
        } catch (err) {
            console.error('Error fetching available tables:', err);
            setError('Failed to fetch available tables');
        } finally {
            setLoadingAvailableTables(false);
        }
    };

    const handleEditClick = (): void => setShowEditModal(true);
    const closeModal = (): void => setShowEditModal(false);
    const handleAddTableClick = (): void => {
        setShowAddTableModal(true);
        fetchAvailableTables();
    };
    const closeAddTableModal = (): void => {
        setShowAddTableModal(false);
        setSelectedTablesToAdd([]);
    };

    const handleTableSelection = (tableId: number): void => {
        setSelectedTablesToAdd(prev =>
            prev.includes(tableId) ? prev.filter(id => id !== tableId) : [...prev, tableId]
        );
    };

    const handleAddSelectedTables = async (): Promise<void> => {
        if (selectedTablesToAdd.length === 0) {
            alert('Please select at least one table to add.');
            return;
        }
        try {
            setLoading(true);
            for (const tableId of selectedTablesToAdd) {
                const table = availableTables.find(t => t.id === tableId);
                if (table) {
                    await TablesService.addTableToMatter({
                        ct_id: table.id,
                        matterid: matterId,
                        table_name: table.table_name || '',
                    });
                }
            }
            await fetchCustomTables();
            closeAddTableModal();
            alert(`Successfully added ${selectedTablesToAdd.length} table(s) to the matter!`);
        } catch (err) {
            console.error('Error adding tables:', err);
            alert('Failed to add some tables. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTable = async (event: React.MouseEvent, table: CustomTableHolding, idx: number): Promise<void> => {
        event.stopPropagation();
        if (window.confirm(`Are you sure you want to delete the table "${table.table_name}"?`)) {
            try {
                await TablesService.deleteCustomTableHolding(table.id);
                const updatedTables = customTables.filter((_, index) => index !== idx);
                const sortedTables = [...updatedTables].sort((a, b) => {
                    const nameA = (a.table_name || '').toLowerCase();
                    const nameB = (b.table_name || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                });
                setCustomTables(sortedTables);
                if (selectedIdx === idx) setSelectedIdx(0);
                else if (selectedIdx > idx) setSelectedIdx(selectedIdx - 1);
                alert('Table deleted successfully!');
            } catch (err) {
                console.error('Error deleting table:', err);
                alert('Failed to delete table. Please try again.');
            }
        }
    };

    if (error) {
        return <div className="CustomTables-error">{error}</div>;
    }

    return (
        <>
            <div className="custom-tables-banner-toolbar">
                <div
                    ref={bannerScrollRef}
                    className="custom-tables-banner-scroll"
                    role="tablist"
                    aria-label="Checklist tables"
                >
                    {customTables.map((table, idx) => (
                        <button
                            type="button"
                            key={table.id ?? table.table_name}
                            role="tab"
                            aria-selected={selectedIdx === idx}
                            className={`custom-tables-banner-tab ${selectedIdx === idx ? 'is-selected' : ''}`}
                            onClick={() => setSelectedIdx(idx)}
                            title={formatTableName(table.table_name)}
                        >
                            {formatTableName(table.table_name)}
                        </button>
                    ))}
                </div>
                <button
                    type="button"
                    className="custom-tables-banner-edit"
                    onClick={handleEditClick}
                    title="Edit table"
                >
                    <FontAwesomeIcon icon={faEdit} />
                </button>
            </div>

            {showEditModal && (
                <div className="CustomTables-modal">
                    <div className="CustomTables-modal-content">
                        <div className="CustomTables-modal-header">
                            <h3 className="CustomTables-modal-title" style={{ color: '#545465' }}>List of tables in this project</h3>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button type="button" className="CustomTables-modal-add" onClick={() => handleAddTableClick()} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', color: '#545465', fontSize: '16px', marginRight: '8px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Add new table">
                                    <FontAwesomeIcon icon={faPlus} />
                                </button>
                                <button type="button" className="CustomTables-modal-close" onClick={closeModal}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>
                        </div>
                        <div className="CustomTables-modal-body">
                            {customTables.length === 0 ? (
                                <p className="CustomTables-empty">No tables found for this matter.</p>
                            ) : (
                                <ul className="template-list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                                    {customTables.map((table, idx) => (
                                        <li
                                            key={table.id ?? table.table_name}
                                            className={`template-list-item ${selectedIdx === idx ? 'selected' : ''}`}
                                            style={{
                                                display: 'flex', alignItems: 'center',
                                                backgroundColor: selectedIdx === idx ? '#f8f9fa' : '#fff',
                                                marginBottom: '8px', padding: '10px', borderRadius: '8px', cursor: 'pointer',
                                                border: selectedIdx === idx ? '2px solid #007bff' : '1px solid #e9ecef',
                                            }}
                                            onClick={() => { setSelectedIdx(idx); closeModal(); }}
                                        >
                                            <div className="circle-badge" style={{ minWidth: '35px', minHeight: '35px', borderRadius: '50%', marginRight: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: 'bold', color: '#fff', fontSize: '0.9rem', background: 'linear-gradient(75deg, rgba(0, 0, 190, 0.557)0%, #bf00dcb7 100%)', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                                                {getTableAbbreviation(table.table_name)}
                                            </div>
                                            <span className="template-name" style={{ color: '#545465', fontSize: '16px', fontWeight: '500', flex: 1 }}>
                                                {formatTableName(table.table_name)}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); handleDeleteTable(e, table, idx); }}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', color: '#dc3545', fontSize: '16px', borderRadius: '4px', marginLeft: '8px' }}
                                                title="Delete table"
                                                onMouseEnter={(e) => { (e.target as HTMLElement).style.backgroundColor = '#f8d7da'; (e.target as HTMLElement).style.transform = 'scale(1.1)'; }}
                                                onMouseLeave={(e) => { (e.target as HTMLElement).style.backgroundColor = 'transparent'; (e.target as HTMLElement).style.transform = 'scale(1)'; }}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showAddTableModal && (
                <div className="CustomTables-modal">
                    <div className="CustomTables-modal-content">
                        <div className="CustomTables-modal-header">
                            <h3 className="CustomTables-modal-title" style={{ color: '#545465' }}>Add Custom Tables to {matterName}</h3>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button type="button" className="CustomTables-modal-close" onClick={closeAddTableModal}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>
                        </div>
                        <div className="CustomTables-modal-body">
                            {loadingAvailableTables ? (
                                <div style={{ textAlign: 'center', padding: '20px' }}><p>Loading available tables...</p></div>
                            ) : availableTables.length === 0 ? (
                                <p className="CustomTables-empty">No additional tables available to add to this matter.</p>
                            ) : (
                                <>
                                    <div style={{ marginBottom: '16px', textAlign: 'right' }}>
                                        <button type="button" onClick={() => setSelectedTablesToAdd(availableTables.map(t => t.id))} style={{ background: '#007bff', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', marginRight: '8px' }}>Select All</button>
                                        <button type="button" onClick={() => setSelectedTablesToAdd([])} style={{ background: '#6c757d', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}>Deselect All</button>
                                    </div>
                                    <ul className="template-list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                                        {availableTables.map((table) => {
                                            let columns: unknown[] = [];
                                            try {
                                                columns = typeof table.column_value === 'string' ? JSON.parse(table.column_value) : (Array.isArray(table.column_value) ? table.column_value : []);
                                            } catch {}
                                            return (
                                                <li
                                                    key={table.id}
                                                    className={`template-list-item ${selectedTablesToAdd.includes(table.id) ? 'selected' : ''}`}
                                                    style={{
                                                        display: 'flex', alignItems: 'center',
                                                        backgroundColor: selectedTablesToAdd.includes(table.id) ? '#e3f2fd' : '#fff',
                                                        marginBottom: '8px', padding: '10px', borderRadius: '8px', cursor: 'pointer',
                                                        border: selectedTablesToAdd.includes(table.id) ? '2px solid #007bff' : '1px solid #e9ecef',
                                                    }}
                                                    onClick={() => handleTableSelection(table.id)}
                                                >
                                                    <div className="circle-badge" style={{ minWidth: '35px', minHeight: '35px', borderRadius: '50%', marginRight: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: 'bold', color: '#fff', fontSize: '0.9rem', background: 'linear-gradient(75deg, rgba(0, 0, 190, 0.557)0%, #bf00dcb7 100%)', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                                                        {getTableAbbreviation(table.table_name)}
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <span className="template-name" style={{ color: '#545465', fontSize: '16px', fontWeight: '500', display: 'block' }}>{formatTableName(table.table_name)}</span>
                                                        <span style={{ color: '#888', fontSize: '0.9em' }}>{Array.isArray(columns) ? `Columns: ${columns.length}` : ''}</span>
                                                    </div>
                                                    <input type="checkbox" checked={selectedTablesToAdd.includes(table.id)} onChange={() => handleTableSelection(table.id)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    <div style={{ marginTop: '20px', textAlign: 'right' }}>
                                        <button type="button" onClick={closeAddTableModal} style={{ background: '#6c757d', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', marginRight: '8px' }}>Cancel</button>
                                        <button type="button" onClick={handleAddSelectedTables} disabled={selectedTablesToAdd.length === 0} style={{ background: selectedTablesToAdd.length === 0 ? '#ccc' : '#28a745', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: selectedTablesToAdd.length === 0 ? 'not-allowed' : 'pointer', fontSize: '14px' }}>
                                            Add {selectedTablesToAdd.length > 0 ? `(${selectedTablesToAdd.length})` : ''} Table{selectedTablesToAdd.length !== 1 ? 's' : ''}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CustomTablesScreen;
